import React from "react";
import { Card, CardFooter } from "@/components/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { TableRowActionsWrapper } from "../../components/SimpleTable/TableRowActionsWrapper";
import { RowContextMenuContent } from "../../components/SimpleTable/RowContextMenuContent";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../types";

interface HorizontalCardProps<TData extends BaseEntity> {
  item: TData;
  index: number;
  children: React.ReactNode;
  onCardClick: (item: TData, index: number) => void;
  config?: SimpleTableConfig<TData>;
  hooks?: EntityHooks<TData>;
}

/**
 * HorizontalCard - Displays entity data in a horizontal card format
 * 
 * Features:
 * - Uses TableRowActionsWrapper to render the same actions as table rows
 * - Supports right-click context menu (same as table rows)
 * - Ensures consistent action display across all views (cards, table, etc.)
 */
export function HorizontalCard<TData extends BaseEntity>({
  item,
  index,
  children,
  onCardClick,
  config,
  hooks,
}: HorizontalCardProps<TData>) {
  const hasRowActions =
    hooks &&
    config &&
    config.detailActions &&
    config.detailActions.length > 0;

  const handleClick = () => {
    onCardClick(item, index);
  };

  const cardContent = (
    <Card
      // content-visibility + contain-intrinsic-size let the browser skip layout
      // and paint for off-screen cards in this non-virtualized grid, which is
      // what eliminates the white gap / jank during fast scroll. The intrinsic
      // size reserves the card's approximate height so the scrollbar stays stable.
      className="@container/entity-card group/entity-card flex h-full min-w-0 w-full cursor-pointer flex-col border-border/60 bg-card shadow-sm transition-shadow duration-150 hover:shadow-md hover:border-border [content-visibility:auto] [contain-intrinsic-size:auto_180px]"
      onClick={handleClick}
    >
      {children}
      {hasRowActions && config && hooks && (
        <CardFooter className="mt-auto gap-2 border-t border-border/40 px-4 pt-2.5 pb-2.5">
          <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
            <TableRowActionsWrapper
              item={item}
              actions={config.detailActions ?? []}
              hooks={hooks}
              entityName={config.entity}
              config={config}
              compact={true}
              maxVisibleActions={config.rowActionsMaxPrimaryIcons ?? 2}
            />
          </div>
        </CardFooter>
      )}
    </Card>
  );

  // If we have row actions, wrap in context menu for right-click support
  if (hasRowActions && config && hooks) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {cardContent}
        </ContextMenuTrigger>
        <RowContextMenuContent
          item={item}
          actions={config.detailActions ?? []}
          hooks={hooks}
          entityName={config.entity}
          config={config}
        />
      </ContextMenu>
    );
  }

  return cardContent;
}
